/**
 * Auth shell that switches between 4 broker-pickable variants:
 *   - split           — marketing pitch left, form right (default)
 *   - centered        — single centered card on a brand-gradient backdrop
 *   - fullscreen-hero — full-bleed hero, form floats over it
 *   - minimal         — plain centered form, no marketing
 *
 * The variant comes from `tenant.settings.authLayout`, populated by
 * `/admin/settings?tab=auth-layout`. Falls back to "split" when no
 * tenant is resolved (plain `localhost:3019`).
 */
import { Outlet, Link } from "react-router-dom";
import { useCurrentTenant } from "@/hooks/useTenant";
import type { AuthLayoutVariant } from "@/types/tenantSettings";
import { Brand } from "./Brand";

export function AuthLayout() {
  const { data: tenantInfo } = useCurrentTenant();
  const variant: AuthLayoutVariant =
    tenantInfo?.tenant?.settings?.authLayout ?? "split";
  const tagline = tenantInfo?.tenant?.settings?.branding?.tagline;
  const heroUrl = tenantInfo?.tenant?.settings?.branding?.authBackgroundUrl;

  if (variant === "centered") return <CenteredLayout tagline={tagline} heroUrl={heroUrl} />;
  if (variant === "minimal") return <MinimalLayout />;
  if (variant === "fullscreen-hero") return <FullscreenHeroLayout heroUrl={heroUrl} tagline={tagline} />;
  return <SplitLayout tagline={tagline} heroUrl={heroUrl} />;
}

/* ─── Split (default — marketing left, form right) ─────────────────── */
function SplitLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-bg overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-30 bg-grad-primary" />
        <div className="absolute -bottom-32 -right-24 h-[32rem] w-[32rem] rounded-full blur-3xl opacity-25 bg-grad-accent" />
      </div>
      {heroUrl && (
        <div
          className="hidden lg:block absolute inset-y-0 left-0 w-1/2 -z-10"
          aria-hidden
          style={{
            backgroundImage: `linear-gradient(135deg, hsl(var(--bg) / 0.72), hsl(var(--bg) / 0.45)), url(${heroUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <aside className="hidden lg:flex flex-col justify-between p-10 border-r border-border/60 glass">
        <Brand />
        <div className="space-y-5 max-w-md">
          <h1 className="text-3xl font-semibold tracking-tight text-fg leading-tight">
            {tagline ? (
              <span className="bg-grad-primary bg-clip-text text-transparent">{tagline}</span>
            ) : (
              <>
                Trade global markets,
                <br />
                <span className="bg-grad-primary bg-clip-text text-transparent">the premium way.</span>
              </>
            )}
          </h1>
          <ul className="space-y-2.5 text-sm text-fg-muted">
            <li>· MT5 live &amp; demo accounts — instant setup</li>
            <li>· Fast deposits &amp; withdrawals — Confirmo · On Now · Bank</li>
            <li>· Multi-level IB programme with live commissions</li>
            <li>· Tight spreads · high leverage · 24/5 markets</li>
          </ul>
        </div>
        <p className="text-xs text-fg-subtle">
          © 2026 AXProp · <Link to="/" className="hover:text-fg">axprop.axfintech.io</Link>
        </p>
      </aside>
      <main className="flex flex-col p-6 sm:p-10">
        <div className="lg:hidden mb-8"><Brand /></div>
        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-sm">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

/* ─── Centered — single card on a brand backdrop ───────────────────── */
function CenteredLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div
      className="relative min-h-screen grid place-items-center bg-bg overflow-hidden p-4"
      style={heroUrl ? {
        backgroundImage: `linear-gradient(135deg, hsl(var(--bg) / 0.85), hsl(var(--bg) / 0.65)), url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      } : undefined}
    >
      {!heroUrl && (
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          <div className="absolute inset-0 bg-grad-primary opacity-[0.08]" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[40rem] w-[40rem] rounded-full blur-3xl opacity-25 bg-grad-primary" />
          <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-20 bg-grad-accent" />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border/60 glass p-8 shadow-2xl">
          <div className="flex flex-col items-center text-center mb-6">
            <Brand size="lg" />
            {tagline && (
              <p className="text-sm text-fg-muted mt-3 max-w-xs">{tagline}</p>
            )}
          </div>
          <Outlet />
        </div>
        <p className="text-center text-xs text-fg-subtle mt-4">
          © 2026 AXProp · <Link to="/" className="hover:text-fg">axprop.axfintech.io</Link>
        </p>
      </div>
    </div>
  );
}

/* ─── Fullscreen hero — bg image, card floats over it ──────────────── */
function FullscreenHeroLayout({ heroUrl, tagline }: { heroUrl?: string; tagline?: string }) {
  const bg: React.CSSProperties = heroUrl
    ? {
        backgroundImage: `linear-gradient(135deg, hsl(var(--bg) / 0.85), hsl(var(--bg) / 0.65)), url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  return (
    <div className="relative min-h-screen grid place-items-center bg-bg overflow-hidden p-4" style={bg}>
      {!heroUrl && (
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          {/* Stronger gradients when no auth-bg uploaded — page shouldn't
              feel empty just because the broker hasn't uploaded a hero
              image yet. The tenant's primaryColor drives the gradient. */}
          <div className="absolute inset-0 bg-grad-primary opacity-40" />
          <div className="absolute -top-40 left-1/4 h-[40rem] w-[40rem] rounded-full blur-3xl opacity-50 bg-grad-primary" />
          <div className="absolute -bottom-40 right-1/4 h-[40rem] w-[40rem] rounded-full blur-3xl opacity-40 bg-grad-accent" />
          <div className="absolute inset-0 bg-bg/60" />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-bg-elevated/95 backdrop-blur-xl border border-border/60 p-8 shadow-2xl">
          <div className="flex flex-col items-center text-center mb-6">
            <Brand size="lg" />
            {tagline && (
              <p className="text-sm text-fg-muted mt-3 max-w-xs">{tagline}</p>
            )}
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/* ─── Minimal — bare centered form, no marketing ───────────────────── */
function MinimalLayout() {
  return (
    <div className="min-h-screen grid place-items-center bg-bg p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <Brand size="lg" />
        </div>
        <Outlet />
      </div>
    </div>
  );
}
